import { useState } from "react";
import { ALL_PESTS, SYMPTOM_OPTIONS, type PestOption } from "../../constants";
import { PestIcon } from "../icons/PestIcons";

interface DiagnosticQuizStepProps {
  onPestSelect?: (pestId: string, isAllowed: boolean) => void;
  onSubmit: (symptoms: string[], pestSightings: string[]) => void;
  onBack: () => void;
}

export function DiagnosticQuizStep({ onPestSelect, onSubmit, onBack }: DiagnosticQuizStepProps) {
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [pestSightings, setPestSightings] = useState<string[]>([]);
  const [error, setError] = useState("");

  function toggleSymptom(id: string) {
    setSymptoms(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
    if (error) setError("");
  }

  function togglePest(id: string) {
    const pest = ALL_PESTS.find(p => p.id === id);
    const selecting = !pestSightings.includes(id);
    setPestSightings(prev => (selecting ? [...prev, id] : prev.filter(p => p !== id)));
    if (selecting && onPestSelect) {
      onPestSelect(id, pest?.category === "standard");
    }
    if (error) setError("");
  }

  function handleSubmit() {
    if (symptoms.length === 0 && pestSightings.length === 0) {
      setError("Select at least one symptom or pest sighting to run the diagnostic.");
      return;
    }
    onSubmit(symptoms, pestSightings);
  }

  const standard = ALL_PESTS.filter(p => p.category === "standard");
  const specialty = ALL_PESTS.filter(p => p.category === "specialty");

  return (
    <div className="animate-fade-in p-4 sm:p-5">
      <div className="mb-4 border-l-2 border-emerald-600 pl-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Metric 3</p>
        <h2 className="text-base font-semibold text-slate-900">Pest Diagnostic Quiz</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Identification index based on structural and environmental indicators.
        </p>
      </div>

      <div className="space-y-5">
        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Visible Signs of Damage
          </p>
          <div className="grid gap-1.5">
            {SYMPTOM_OPTIONS.map(symptom => (
              <label
                key={symptom.id}
                className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors ${
                  symptoms.includes(symptom.id)
                    ? "border-emerald-600 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={symptoms.includes(symptom.id)}
                  onChange={() => toggleSymptom(symptom.id)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
                />
                <span className="text-sm text-slate-800">{symptom.label}</span>
              </label>
            ))}
          </div>
        </section>

        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Physical Pest Sightings
          </p>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-emerald-700">Standard Pests</p>
          <div className="mb-3 grid grid-cols-2 gap-1.5">
            {standard.map(pest => (
              <PestToggle key={pest.id} pest={pest} selected={pestSightings.includes(pest.id)} onToggle={togglePest} />
            ))}
          </div>
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">Specialty / Wildlife</p>
          <div className="grid grid-cols-2 gap-1.5">
            {specialty.map(pest => (
              <PestToggle
                key={pest.id}
                pest={pest}
                selected={pestSightings.includes(pest.id)}
                onToggle={togglePest}
                variant="specialty"
              />
            ))}
          </div>
        </section>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 rounded-lg bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-800"
          >
            Generate Diagnostic Report
          </button>
        </div>
      </div>
    </div>
  );
}

function PestToggle({
  pest,
  selected,
  onToggle,
  variant = "standard",
}: {
  pest: PestOption;
  selected: boolean;
  onToggle: (id: string) => void;
  variant?: "standard" | "specialty";
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(pest.id)}
      className={`flex flex-col items-center gap-1.5 rounded-lg border p-2.5 transition-all ${
        selected
          ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600"
          : variant === "specialty"
            ? "border-slate-100 bg-slate-50 hover:border-slate-300"
            : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <PestIcon
        id={pest.id}
        className={`h-6 w-6 ${variant === "specialty" && !selected ? "text-slate-400" : "text-emerald-700"}`}
      />
      <span className="text-center text-[11px] font-medium leading-tight text-slate-800">{pest.label}</span>
    </button>
  );
}
